import { Text, Spinner, Badge, makeStyles, tokens } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { fetchAnomalies, type Anomaly, type AnomalyType, type AnomalySeverity } from '../api/production.api';
import { useEntityNames } from '../hooks/useEntityNames';
import { fetchMermaSummary } from '../api/merma.api';
import { fetchModules } from '../api/modules.api';
import { KpiCard } from '../components/shared/KpiCard';
import { DataTable, type DataTableColumn } from '../components/shared/DataTable';
import { DonutChart, type DonutChartSlice } from '../components/shared/charts/DonutChart';
import { ParcelStatusGrid, type ParcelStatusCell } from '../components/shared/ParcelStatusGrid';
import { useProjectStore } from '../store/project.store';
import { useT } from '../i18n';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { useRanchoStore } from '../store/rancho.store';
import { PageHeader } from '../components/shared/PageHeader';

/**
 * Fase C — "Riesgos": agregación de lectura sobre fuentes ya existentes, sin
 * tabla ni endpoint nuevo. Anomalías tenant-wide (F2, `status=ABIERTA` sin
 * `projectId` — el backend ya soporta esa query, solo `InvestorProductionPage`
 * la usaba acotada a un proyecto) + eventos de merma recientes de mayor
 * impacto (F12, `/merma/summary`, misma fuente que `InvestorYieldPage`).
 *
 * Compliance PHI/REI/MRL queda declarado como gap conocido: los guards
 * bloquean en tiempo real pero no persisten un log consultable — ver
 * docs/superpowers/specs/2026-08-12-fase-c-pantallas-nuevas.md.
 */

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  kpiRow:  { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  splitRow: { display: 'flex', gap: tokens.spacingHorizontalL, flexWrap: 'wrap', alignItems: 'flex-start' },
  splitCol: { flex: '1', minWidth: '280px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  sectionTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  row: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalM,
    padding: '10px 14px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    borderLeft: '4px solid transparent',
  },
  rowInfo: { display: 'flex', flexDirection: 'column', gap: '2px' },
  rowTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase300 },
  rowMeta: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  note: {
    padding: '14px 16px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

const SEVERITY_ORDER: Record<AnomalySeverity, number> = { CRITICA: 0, ALTA: 1, MEDIA: 2, BAJA: 3 };

const SEVERITY_COLOR: Record<AnomalySeverity, 'informative' | 'warning' | 'danger'> = {
  BAJA: 'informative', MEDIA: 'warning', ALTA: 'warning', CRITICA: 'danger',
};

/** Relleno sólido para el heatmap de módulos — mismo criterio de hex fijos que
 * `STATUS_FILL` en `InvestorOverviewPage.tsx` (`ParcelStatusGrid` pinta texto
 * blanco sobre `color`, necesita contraste suficiente). */
const SEVERITY_FILL: Record<AnomalySeverity, string> = {
  BAJA: '#0078d4', MEDIA: '#ca5010', ALTA: '#d83b01', CRITICA: '#a4262c',
};
const NO_RISK_FILL = '#8a8886';

/** Paleta fija de 9 colores para el donut "riesgos por categoría" — un color
 * por `AnomalyType`. No son tokens semánticos (no hay un significado
 * success/danger por tipo de anomalía) así que se fija una paleta categórica
 * simple, consistente con los colores ya usados en otras pantallas del portal. */
const TYPE_COLOR: Record<AnomalyType, string> = {
  DESVIACION_FENOLOGICA: '#0078d4',
  PLAGA:      '#d83b01',
  RIEGO:      '#0099bc',
  MAQUINARIA: '#8764b8',
  PERSONAL:   '#c19c00',
  CLIMA:      '#ca5010',
  CALIDAD:    '#a4262c',
  SENSOR:     '#498205',
  OTRO:       '#8a8886',
};

export function RiesgosPage() {
  const s = useStyles();
  const t = useT();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { formatCurrency, formatDate } = useLocaleSettings();
  const { loteName } = useEntityNames();
  const activeRanchoId = useRanchoStore(st => st.activeRanchoId);
  const activeProjectId = useProjectStore(st => st.activeProjectId);

  const { data: anomalies = [], isLoading: anomaliesLoading } = useQuery({
    queryKey: ['riesgos-anomalies', activeRanchoId],
    queryFn: () => fetchAnomalies({ status: 'ABIERTA', ranchoId: activeRanchoId }),
  });

  const { data: merma, isLoading: mermaLoading } = useQuery({
    queryKey: ['riesgos-merma-summary', activeRanchoId],
    queryFn: () => fetchMermaSummary(activeRanchoId),
  });

  const { data: modules = [] } = useQuery({
    queryKey: ['riesgos-modules', activeProjectId, activeRanchoId],
    queryFn: () => fetchModules({ projectId: activeProjectId!, ranchoId: activeRanchoId }),
    enabled: !!activeProjectId,
  });

  const sortedAnomalies = [...anomalies].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);

  const topMerma = merma
    ? [...merma.records].sort((a, b) => (b.wasteImpactCost ?? 0) - (a.wasteImpactCost ?? 0)).slice(0, 8)
    : [];

  function anomalyTypeLabel(tipo: AnomalyType): string {
    return t.investorProduction[`anomalyType${tipo}` as keyof typeof t.investorProduction] as string;
  }

  function anomalySeverityLabel(sev: AnomalySeverity): string {
    return t.investorProduction[`severity${sev}` as keyof typeof t.investorProduction] as string;
  }

  function anomalyStatusLabel(status: Anomaly['status']): string {
    return t.riesgos[`status${status}` as keyof typeof t.riesgos] as string;
  }

  // KPIs — derivados de las anomalías abiertas + el resumen de merma ya cargados, sin endpoint nuevo.
  const countBySeverity = (sev: AnomalySeverity) => anomalies.filter(a => a.severity === sev).length;
  const withResponsible = anomalies.filter(a => !!a.responsibleUserId).length;

  // Mapa de riesgo por módulo — severidad más alta entre las anomalías abiertas de cada módulo.
  const moduleCells: ParcelStatusCell[] = modules.map(m => {
    const moduleAnomalies = anomalies.filter(a => a.moduleId === m.moduleId);
    const worst = moduleAnomalies.length
      ? moduleAnomalies.reduce((w, a) => (SEVERITY_ORDER[a.severity] < SEVERITY_ORDER[w.severity] ? a : w)).severity
      : null;
    return { key: m.moduleId, label: m.moduleCode, color: worst ? SEVERITY_FILL[worst] : NO_RISK_FILL };
  });
  const moduleRiskLegend = [
    ...(Object.keys(SEVERITY_FILL) as AnomalySeverity[]).map(sev => ({ label: anomalySeverityLabel(sev), color: SEVERITY_FILL[sev] })),
    { label: t.riesgos.noRisk, color: NO_RISK_FILL },
  ];

  // Riesgos por categoría — donut sobre el tipo de las anomalías abiertas.
  const typeCounts = (Object.keys(TYPE_COLOR) as AnomalyType[])
    .map(tipo => ({ tipo, count: anomalies.filter(a => a.tipo === tipo).length }))
    .filter(x => x.count > 0);
  const donutData: DonutChartSlice[] = typeCounts.map(x => ({
    label: anomalyTypeLabel(x.tipo),
    color: TYPE_COLOR[x.tipo],
    pct: anomalies.length ? Math.round((x.count / anomalies.length) * 1000) / 10 : 0,
  }));

  const tableColumns: DataTableColumn<Anomaly>[] = [
    { key: 'detectedAt', header: t.riesgos.colDate, render: a => formatDate(a.detectedAt.slice(0, 10)) },
    { key: 'tipo', header: t.riesgos.colCategory, render: a => anomalyTypeLabel(a.tipo) },
    { key: 'title', header: t.riesgos.colDescription, width: 1.6, render: a => a.description ? `${a.title} — ${a.description}` : a.title },
    { key: 'severity', header: t.riesgos.colLevel, render: a => <Badge appearance="tint" color={SEVERITY_COLOR[a.severity]}>{anomalySeverityLabel(a.severity)}</Badge> },
    { key: 'responsibleUserId', header: t.riesgos.colResponsible, render: a => a.responsibleUserId ?? '—' },
    { key: 'status', header: t.riesgos.colStatus, render: a => anomalyStatusLabel(a.status) },
  ];

  return (
    <div className={s.root}>
      <PageHeader title={t.riesgos.title} subtitle={t.riesgos.subtitle} />

      {!anomaliesLoading && (
        <div className={s.kpiRow}>
          <KpiCard label={t.riesgos.kpiOpen} value={anomalies.length} />
          <KpiCard label={t.riesgos.kpiCritical} value={countBySeverity('CRITICA')} tone={countBySeverity('CRITICA') > 0 ? 'danger' : 'default'} />
          <KpiCard label={t.riesgos.kpiHigh} value={countBySeverity('ALTA')} />
          <KpiCard label={t.riesgos.kpiMedium} value={countBySeverity('MEDIA')} />
          <KpiCard label={t.riesgos.kpiLow} value={countBySeverity('BAJA')} />
          <KpiCard label={t.riesgos.kpiWithResponsible} value={withResponsible} />
          {merma && <KpiCard label={t.riesgos.kpiMermaImpact} value={formatCurrency(merma.kpi.totalWasteImpactCost ?? 0)} />}
        </div>
      )}

      <div className={s.splitRow}>
        <div className={s.splitCol}>
          <span className={s.sectionTitle}>{t.riesgos.moduleRiskTitle}</span>
          {!activeProjectId && <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.riesgos.noActiveProject}</Text>}
          {activeProjectId && moduleCells.length > 0 && (
            <ParcelStatusGrid
              columns={8}
              cellHeight={44}
              cells={moduleCells}
              legend={moduleRiskLegend}
              onCellClick={(cell: ParcelStatusCell) => navigate(`/${tenantId}/fincas/modulo/${cell.key}`)}
            />
          )}
        </div>
        <div className={s.splitCol}>
          <span className={s.sectionTitle}>{t.riesgos.categoryTitle}</span>
          {donutData.length === 0 && <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.riesgos.noAnomalies}</Text>}
          {donutData.length > 0 && (
            <DonutChart data={donutData} centerValue={String(anomalies.length)} centerLabel={t.riesgos.kpiOpen} />
          )}
        </div>
      </div>

      {/* Registro de riesgos y alertas — tenant-wide */}
      <div className={s.section}>
        <span className={s.sectionTitle}>{t.riesgos.tableTitle}</span>
        {anomaliesLoading && <Spinner size="small" label={t.riesgos.loadingAnomalies} />}
        {!anomaliesLoading && (
          <DataTable
            columns={tableColumns}
            rows={sortedAnomalies}
            rowKey={a => `${a.projectId}#${a.anomalyKey}`}
            emptyMessage={t.riesgos.noAnomalies}
          />
        )}
      </div>

      {/* Merma reciente de mayor impacto económico */}
      <div className={s.section}>
        <span className={s.sectionTitle}>{t.riesgos.mermaTitle}</span>
        {mermaLoading && <Spinner size="small" label={t.riesgos.loadingMerma} />}
        {!mermaLoading && topMerma.length === 0 && <Text size={200}>{t.riesgos.noMerma}</Text>}
        {topMerma.map(r => (
          <div key={r.mermaId} className={s.row}>
            <div className={s.rowInfo}>
              <span className={s.rowTitle}>{loteName(r.loteId)}</span>
              <span className={s.rowMeta}>
                {t.merma[`reason${r.reason}` as keyof typeof t.merma]} · {(r.rejectedKg ?? 0).toFixed(1)} kg
              </span>
            </div>
            <Badge appearance="tint" color={(r.wasteImpactCost ?? 0) > 0 ? 'danger' : 'informative'}>
              {t.riesgos.impact}: {formatCurrency(r.wasteImpactCost ?? 0)}
            </Badge>
          </div>
        ))}
      </div>

      {/* Compliance — gap declarado, no inventado */}
      <div className={s.section}>
        <span className={s.sectionTitle}>{t.riesgos.complianceTitle}</span>
        <div className={s.note}>{t.riesgos.complianceNote}</div>
      </div>
    </div>
  );
}
